import { useTranslation } from '@client-app/i18n';
import { useAdminContext } from '@/contexts/AdminContext';
import { Permission, useAuth } from '@/contexts/AuthContext';
import { refreshProfileFromServer } from '@/contexts/AuthContext/refreshProfileFromServer';
import { profileMatchesPaymentReturnPlan } from '@/contexts/AuthContext/paymentReturnPlanSync';
import {
    readPaymentReturnParams,
    type PaymentReturnParams,
} from '@/utils/paymentReturnParams';
import { billingService, BillingTransaction, BillingTransactionsResponse } from '@common/api/billingService';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveBillingAccessViewState } from './billingAccessState';

// Simple Skeleton for Tailwind
const Skeleton = ({ className }: { className?: string }) => (
    <div className={clsx("animate-pulse bg-gray-200 rounded", className)} />
);

const BillingSettingsSkeleton = ({ label }: { label: string }) => (
    <div className="space-y-8" aria-live="polite" aria-label={label}>
        <div className="space-y-4">
            <Skeleton className="h-5 w-28" />
            <div className="rounded-[20px] border border-[#E8EBF3] bg-white p-6 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                <div className="flex items-start justify-between gap-6">
                    <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-7 w-28" />
                            <Skeleton className="h-6 w-16 rounded-full" />
                        </div>
                        <Skeleton className="h-5 w-72 max-w-full" />
                    </div>
                    <Skeleton className="h-10 w-24 rounded-[10px]" />
                </div>
            </div>
        </div>
        <div className="space-y-4">
            <Skeleton className="h-5 w-32" />
            <div className="space-y-3 rounded-[20px] border border-[#E8EBF3] bg-white p-6 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                <Skeleton className="h-11 w-full" />
                <Skeleton className="h-[52px] w-full" />
                <Skeleton className="h-[52px] w-full" />
                <Skeleton className="h-[52px] w-full" />
            </div>
        </div>
    </div>
);

const formatPlanBadgeLabel = (value?: string | null) => {
    const normalized = (value || '').trim();
    if (!normalized) return '';

    return normalized
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, match => match.toUpperCase());
};

interface BillingSettingsProps {
    onClose: () => void;
}

const BILLING_HISTORY_ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGE_BUTTONS = 5;
const PAYMENT_RETURN_TRANSACTION_REFRESH_ATTEMPTS = 10;
const PAYMENT_RETURN_TRANSACTION_REFRESH_DELAY_MS = 1500;
const PAYMENT_RETURN_PLAN_REFRESH_ATTEMPTS = 6;
const PAYMENT_RETURN_PLAN_REFRESH_DELAY_MS = 1500;

const formatIntegerForDisplay = (value: number, locale: string = 'en') => {
    const normalized = Number.isFinite(value) ? Math.trunc(value) : 0;
    return new Intl.NumberFormat(locale).format(Math.max(0, normalized));
};

const wait = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

const buildBillingTransactionRefreshSignature = (
    rows: BillingTransaction[],
    pagination?: BillingTransactionsResponse['pagination'],
) => {
    const firstRow = rows[0];
    return [
        pagination?.total ?? rows.length,
        firstRow?.id || '',
        firstRow?.date || '',
        firstRow?.description || '',
        firstRow?.amountCents ?? '',
        firstRow?.status || '',
    ].join('|');
};

const readBillingPaymentReturnEventDetail = (event: Event): PaymentReturnParams | null => {
    const detail = (event as CustomEvent<PaymentReturnParams>).detail;
    return detail?.result === 'success' ? detail : null;
};

const BillingSettings: React.FC<BillingSettingsProps> = ({ onClose }) => {
    const { locale, t } = useTranslation();
    const {
        userBilling, permissions, currentWorkspace, prefetchedBillingWorkspaceId,
        billingPrefetchingWorkspaceId, profile, setProfile, refreshWorkspaces,
        applyWorkspaceSubscriptionPlan, isLoadingProfile, isLoadingWorkspaces
    } = useAuth();
    const { modal } = useAdminContext();

    const canViewSubscription = permissions.checkPermissions([Permission.SUBSCRIPTION_VIEW]);
    const canManageSubscription = permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]);
    const canView = permissions.isOwner || canViewSubscription;
    const canManage = permissions.isOwner || canManageSubscription;
    const billingAccessViewState = resolveBillingAccessViewState({
        workspaceId: currentWorkspace?.id,
        isLoadingProfile,
        isLoadingWorkspaces,
        permissionStatus: permissions.status,
        canView,
    });
    const canLoadBilling = billingAccessViewState === 'ready';

    const {
        invoices, loading, planText, fetchInvoices, refreshAll, applyWorkspacePlan
    } = userBilling;
    const { invoices: data, pagination } = invoices || {};
    const isInitialInvoiceLoading = loading.invoices && (!data || data.length === 0);
    const [billingTransactions, setBillingTransactions] = useState<BillingTransactionsResponse | null>(null);
    const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
    const [isPaymentReturnTransactionsRefreshing, setIsPaymentReturnTransactionsRefreshing] = useState(false);
    const [billingHistoryPage, setBillingHistoryPage] = useState(1);
    const paymentReturnRefreshKeyRef = useRef<string | null>(null);
    const previousAccessViewStateRef = useRef<string | null>(null);
    const isMountedRef = useRef(true);
    const transactionRows = billingTransactions?.transactions || [];
    const transactionPagination = billingTransactions?.pagination;
    const isInitialTransactionsLoading = isTransactionsLoading && transactionRows.length === 0;
    const isBillingHistoryAwaitingPaymentReturn = isPaymentReturnTransactionsRefreshing && transactionRows.length === 0;
    const shouldShowBillingHistorySkeleton = isInitialTransactionsLoading
        || isInitialInvoiceLoading
        || isBillingHistoryAwaitingPaymentReturn;
    const hasInvoicesForCurrentWorkspace = Boolean(
        currentWorkspace?.id
            && userBilling.invoicesWorkspaceId === currentWorkspace.id
            && invoices
    );
    const hasPrefetchedBillingForCurrentWorkspace = Boolean(
        currentWorkspace?.id
            && prefetchedBillingWorkspaceId === currentWorkspace.id
    );
    const isBillingPrefetchingCurrentWorkspace = Boolean(
        currentWorkspace?.id
            && billingPrefetchingWorkspaceId === currentWorkspace.id
            && prefetchedBillingWorkspaceId !== currentWorkspace.id
    );
    const shouldShowPlanSkeleton = isBillingPrefetchingCurrentWorkspace;

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (previousAccessViewStateRef.current === billingAccessViewState) {
            return;
        }

        previousAccessViewStateRef.current = billingAccessViewState;
        console.info('[BillingSettings] Billing access state changed', {
            route_kind: 'settings_billing',
            current_state: billingAccessViewState,
            transition_reason: 'workspace_permission_resolution',
            workspace_id_present: Boolean(currentWorkspace?.id),
            permission_status: permissions.status,
        });
    }, [billingAccessViewState, currentWorkspace?.id, permissions.status]);

    useEffect(() => {
        if (!canLoadBilling || !currentWorkspace?.id) return;
        if (hasInvoicesForCurrentWorkspace || isBillingPrefetchingCurrentWorkspace) return;

        fetchInvoices(1, BILLING_HISTORY_ITEMS_PER_PAGE, currentWorkspace.id);
    }, [
        canLoadBilling,
        currentWorkspace?.id,
        fetchInvoices,
        hasInvoicesForCurrentWorkspace,
        hasPrefetchedBillingForCurrentWorkspace,
        isBillingPrefetchingCurrentWorkspace
    ]);

    useEffect(() => {
        setBillingHistoryPage(1);
    }, [currentWorkspace?.id]);

    const fetchBillingTransactions = useCallback(async (
        options: { page?: number; showLoading?: boolean } = {},
    ): Promise<BillingTransactionsResponse | null> => {
        if (!canLoadBilling || !currentWorkspace?.id) {
            setBillingTransactions(null);
            return null;
        }

        const page = options.page || billingHistoryPage;
        const showLoading = options.showLoading !== false;

        if (showLoading && isMountedRef.current) {
            setIsTransactionsLoading(true);
        }

        try {
            const response = await billingService.getBillingTransactions({
                page,
                size: BILLING_HISTORY_ITEMS_PER_PAGE,
            });
            if (isMountedRef.current) {
                setBillingTransactions(response);
            }
            return response;
        } catch (error) {
            console.error('Failed to fetch billing transactions:', error);
            if (isMountedRef.current) {
                setBillingTransactions(null);
            }
            return null;
        } finally {
            if (showLoading && isMountedRef.current) {
                setIsTransactionsLoading(false);
            }
        }
    }, [billingHistoryPage, canLoadBilling, currentWorkspace?.id]);

    useEffect(() => {
        let isActive = true;

        const loadBillingTransactions = async () => {
            const response = await fetchBillingTransactions();
            if (!isActive || response) {
                return;
            }
        };

        void loadBillingTransactions();

        return () => {
            isActive = false;
        };
    }, [fetchBillingTransactions]);

    const refreshBillingTransactionsAfterPaymentReturn = useCallback(async () => {
        if (!canLoadBilling || !currentWorkspace?.id) {
            return;
        }

        if (billingHistoryPage !== 1) {
            setBillingHistoryPage(1);
        }

        const initialTransactionSignature = buildBillingTransactionRefreshSignature(
            transactionRows,
            transactionPagination,
        );
        const hasInitialTransactionRows = transactionRows.length > 0;

        setIsPaymentReturnTransactionsRefreshing(true);
        try {
            for (let attempt = 0; attempt < PAYMENT_RETURN_TRANSACTION_REFRESH_ATTEMPTS; attempt += 1) {
                const response = await fetchBillingTransactions({
                    page: 1,
                    showLoading: attempt === 0,
                });
                const rows = response?.transactions || [];
                const nextTransactionSignature = buildBillingTransactionRefreshSignature(
                    rows,
                    response?.pagination,
                );
                const hasNewBillingHistory = rows.length > 0
                    && (!hasInitialTransactionRows || nextTransactionSignature !== initialTransactionSignature);

                if (hasNewBillingHistory) {
                    break;
                }

                if (attempt < PAYMENT_RETURN_TRANSACTION_REFRESH_ATTEMPTS - 1) {
                    await wait(PAYMENT_RETURN_TRANSACTION_REFRESH_DELAY_MS);
                }
            }
        } finally {
            if (isMountedRef.current) {
                setIsPaymentReturnTransactionsRefreshing(false);
            }
        }
    }, [
        billingHistoryPage,
        canLoadBilling,
        currentWorkspace?.id,
        fetchBillingTransactions,
        transactionPagination,
        transactionRows,
    ]);

    const refreshBillingPlanAfterPaymentReturn = useCallback(async (
        paymentReturn: PaymentReturnParams,
    ) => {
        if (!canLoadBilling || !currentWorkspace?.id) {
            return;
        }

        try {
            const workspaceId = currentWorkspace.id;
            for (let attempt = 0; attempt < PAYMENT_RETURN_PLAN_REFRESH_ATTEMPTS; attempt += 1) {
                const latestProfile = await refreshProfileFromServer({
                    workspaceId,
                    includeSetupCardStatus: true,
                });
                if (!isMountedRef.current) {
                    return;
                }

                const profileMatchesReturn = profileMatchesPaymentReturnPlan(
                    latestProfile,
                    paymentReturn.planCode,
                    paymentReturn.billingCycle,
                );
                const profileStatusIsActiveForReturn = !paymentReturn.requireActiveStatus
                    || !['trial', 'trialing'].includes(String(latestProfile?.workspacePlan?.status || '').toLowerCase());
                const shouldApplyProfile = Boolean(latestProfile && profileMatchesReturn && profileStatusIsActiveForReturn);
                const shouldApplyLastAttemptFallback = Boolean(
                    latestProfile
                    && !paymentReturn.requireActiveStatus
                    && attempt === PAYMENT_RETURN_PLAN_REFRESH_ATTEMPTS - 1,
                );

                if (shouldApplyProfile || shouldApplyLastAttemptFallback) {
                    setProfile(latestProfile);
                    applyWorkspacePlan(latestProfile.workspacePlan);
                    if (latestProfile.workspacePlan?.planCode) {
                        applyWorkspaceSubscriptionPlan(
                            workspaceId,
                            latestProfile.workspacePlan.planCode,
                            latestProfile.workspacePlan.planName,
                            latestProfile.workspacePlan.billingCycle || paymentReturn.billingCycle,
                        );
                    }
                }

                if (shouldApplyProfile) {
                    break;
                }

                if (attempt < PAYMENT_RETURN_PLAN_REFRESH_ATTEMPTS - 1) {
                    await wait(PAYMENT_RETURN_PLAN_REFRESH_DELAY_MS);
                }
            }

            await Promise.allSettled([
                refreshAll(),
                refreshWorkspaces({
                    silent: true,
                    skipEmptyRedirect: true,
                    preferredWorkspaceId: workspaceId,
                }),
            ]);
        } catch (error) {
            console.error('Failed to refresh billing plan after payment return:', error);
        }
    }, [
        applyWorkspacePlan,
        applyWorkspaceSubscriptionPlan,
        canLoadBilling,
        currentWorkspace?.id,
        refreshAll,
        refreshWorkspaces,
        setProfile,
    ]);

    const refreshBillingAfterPaymentReturn = useCallback(async (
        transitionReason: 'payment_return_event' | 'payment_return_url',
        paymentReturnOverride?: PaymentReturnParams | null,
    ) => {
        if (!canLoadBilling || !currentWorkspace?.id || typeof window === 'undefined') {
            return;
        }

        const paymentReturn = paymentReturnOverride || readPaymentReturnParams(
            new URLSearchParams(window.location.search),
            {
                workspaceId: currentWorkspace.id,
                planCode: profile?.workspacePlan?.planCode,
                planName: profile?.workspacePlan?.planName,
                billingCycle: profile?.workspacePlan?.billingCycle,
            },
        );
        if (paymentReturn.result !== 'success') {
            return;
        }

        const refreshKey = [
            currentWorkspace.id,
            paymentReturn.result,
            paymentReturn.planCode || '',
            paymentReturn.planName || '',
            paymentReturn.billingCycle || '',
            paymentReturn.checked ? 'checked' : 'unchecked',
            paymentReturn.requireActiveStatus ? 'require-active' : 'allow-any-status',
        ].join(':');
        if (paymentReturnRefreshKeyRef.current === refreshKey) {
            return;
        }

        paymentReturnRefreshKeyRef.current = refreshKey;
        console.info('[BillingSettings] Payment return refresh started', {
            route_kind: 'settings_billing',
            current_state: 'payment_return_refreshing',
            transition_reason: transitionReason,
            workspace_id_present: Boolean(paymentReturn.workspaceId),
            payment_checked: paymentReturn.checked,
            billing_cycle: paymentReturn.billingCycle,
        });
        await Promise.allSettled([
            refreshBillingPlanAfterPaymentReturn(paymentReturn),
            refreshBillingTransactionsAfterPaymentReturn(),
        ]);
    }, [
        canLoadBilling,
        currentWorkspace?.id,
        profile?.workspacePlan?.planCode,
        profile?.workspacePlan?.planName,
        profile?.workspacePlan?.billingCycle,
        refreshBillingPlanAfterPaymentReturn,
        refreshBillingTransactionsAfterPaymentReturn,
    ]);

    useEffect(() => {
        const handlePaymentReturnSuccess = (event: Event) => {
            void refreshBillingAfterPaymentReturn(
                'payment_return_event',
                readBillingPaymentReturnEventDetail(event),
            );
        };

        window.addEventListener('billing:payment-return-success', handlePaymentReturnSuccess);
        return () => {
            window.removeEventListener('billing:payment-return-success', handlePaymentReturnSuccess);
        };
    }, [refreshBillingAfterPaymentReturn]);

    useEffect(() => {
        if (!canLoadBilling || !currentWorkspace?.id) {
            return;
        }

        const paymentReturn = readPaymentReturnParams(
            new URLSearchParams(window.location.search),
            {
                workspaceId: currentWorkspace.id,
                planCode: profile?.workspacePlan?.planCode,
                planName: profile?.workspacePlan?.planName,
                billingCycle: profile?.workspacePlan?.billingCycle,
            },
        );
        if (paymentReturn.result !== 'success') {
            return;
        }

        void refreshBillingAfterPaymentReturn('payment_return_url');
    }, [
        canLoadBilling,
        currentWorkspace?.id,
        profile?.workspacePlan?.planCode,
        profile?.workspacePlan?.planName,
        profile?.workspacePlan?.billingCycle,
        refreshBillingAfterPaymentReturn,
    ]);

    const totalBillingHistoryItems = Math.max(
        0,
        Number(transactionPagination?.total || transactionRows.length || 0),
    );
    const totalBillingHistoryPages = Math.max(
        1,
        Number(
            transactionPagination?.totalPages ||
                Math.ceil(totalBillingHistoryItems / BILLING_HISTORY_ITEMS_PER_PAGE),
        ),
    );
    const visibleBillingHistoryPages = useMemo(() => {
        if (totalBillingHistoryPages <= MAX_VISIBLE_PAGE_BUTTONS) {
            return Array.from({ length: totalBillingHistoryPages }, (_, idx) => idx + 1);
        }

        const half = Math.floor(MAX_VISIBLE_PAGE_BUTTONS / 2);
        let start = Math.max(1, billingHistoryPage - half);
        let end = start + MAX_VISIBLE_PAGE_BUTTONS - 1;

        if (end > totalBillingHistoryPages) {
            end = totalBillingHistoryPages;
            start = end - MAX_VISIBLE_PAGE_BUTTONS + 1;
        }

        return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
    }, [billingHistoryPage, totalBillingHistoryPages]);

    const formatCurrency = (item: BillingTransaction) => {
        const currency = (item.currency || 'usd').toUpperCase();
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(item.amountCents || 0) / 100);
    };

    return (
        <div className="space-y-8">
            {billingAccessViewState === 'loading' ? (
                <BillingSettingsSkeleton label={t('website.billing.billingAccessLoading')} />
            ) : billingAccessViewState === 'error' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <p className="text-lg font-medium text-[#1A162F]">
                        {t('website.billing.billingAccessLoadFailed')}
                    </p>
                    <p className="mt-2 text-sm text-[#666F8D]">
                        {t('website.billing.billingAccessLoadFailedDescription')}
                    </p>
                    <button
                        type="button"
                        onClick={() => void permissions.refresh()}
                        className="mt-5 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
                    >
                        {t('common.retry')}
                    </button>
                </div>
            ) : billingAccessViewState === 'permission-denied' ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                    <p className="text-lg font-medium">{t('website.billing.permissionDenied')}</p>
                    <p className="text-sm mt-2">{t('website.billing.noPermissionViewSubscriptionDetails')}</p>
                </div>
            ) : (
                <>
                    {/* Current Plan */}
                    <div className="space-y-4">
                        <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">{t('website.billing.currentPlan')}</h3>
                        <div className="relative overflow-hidden rounded-[20px] border border-[#E8EBF3] bg-white p-6 shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                            {shouldShowPlanSkeleton ? (
                                <div
                                    className="flex justify-between items-start"
                                    aria-live="polite"
                                    aria-label={t('website.billing.currentBillingPlanLoading')}
                                >
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="h-7 w-24" />
                                            <Skeleton className="h-6 w-14 rounded-full" />
                                        </div>
                                        <Skeleton className="h-5 w-72 max-w-full" />
                                    </div>
                                    <Skeleton className="h-10 w-24 rounded-lg" />
                                </div>
                            ) : (
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h4 className="text-[18px] font-medium tracking-[-0.01em] text-[#1A162F]">
                                                {planText.planDisplayText}
                                            </h4>
                                            {planText.badge ? (
                                                <span className={clsx(
                                                    "rounded-full px-2 py-0.5 text-[10px] font-medium",
                                                    planText.status === 'trial'
                                                        ? "bg-amber-100 text-amber-700"
                                                        : "bg-[#F4F5F8] text-[#666F8D]"
                                                )}>
                                                    {formatPlanBadgeLabel(planText.badge)}
                                                </span>
                                            ) : null}
                                        </div>
                                        <p className="text-sm font-normal text-[#666F8D]">
                                            {planText.expirationText}
                                        </p>
                                    </div>

                                    <button
                                        onClick={() => {
                                            if (!canManage) return;
                                            modal.openModal('pricing');
                                            onClose();
                                        }}
                                        disabled={!canManage}
                                        className={clsx(
                                            "rounded-[10px] px-4 py-2.5 text-sm font-medium transition-colors",
                                            canManage
                                                ? "bg-[#1A162F] text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] hover:bg-primary/90"
                                                : "cursor-not-allowed border border-[#E8EBF3] bg-[#F8F9FC] text-[#8A93B2]"
                                        )}
                                    >
                                        {canManage ? planText.buttonText : t('website.billing.viewOnly')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
  
                    {/* Billing History */}
                    <div className="space-y-4">
                        <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">{t('website.billing.billingHistory')}</h3>
                        <div className="overflow-hidden rounded-[20px] border border-[#E8EBF3] bg-white shadow-[0_10px_24px_rgba(26,22,47,0.06)]">
                            <div className="overflow-x-auto">
                            <div className="min-w-[760px]">
                            {/* Table Header */}
                            <div className="grid grid-cols-[1fr_1.6fr_1.1fr_0.9fr_0.8fr] border-b border-[#ECEFF5] bg-[#FCFCFC] px-6 py-4">
                                <div className="text-xs font-medium text-[#8A93B2]">{t('website.billing.date')}</div>
                                <div className="text-xs font-medium text-[#8A93B2]">{t('website.billing.description')}</div>
                                <div className="text-xs font-medium text-[#8A93B2]">{t('website.billing.type')}</div>
                                <div className="text-right text-xs font-medium text-[#8A93B2]">{t('website.billing.amount')}</div>
                                <div className="text-right text-xs font-medium text-[#8A93B2]">{t('website.billing.status')}</div>
                            </div>
            
                            <div className="divide-y divide-[#ECEFF5]">
                                {transactionRows.length > 0 ? (
                                    transactionRows.map((item) => (
                                        <div key={item.id} className="grid grid-cols-[1fr_1.6fr_1.1fr_0.9fr_0.8fr] px-6 py-4 transition-colors hover:bg-[#F8F9FC]">
                                            <div className="text-sm font-normal text-[#666F8D]">
                                                {item.date ? dayjs(item.date).format('YYYY-MM-DD') : '--'}
                                            </div>
                                            <div className="truncate pr-4 text-sm font-normal text-[#1A162F]">{item.description || '--'}</div>
                                            <div className="text-sm font-normal text-[#666F8D]">{item.type || '--'}</div>
                                            <div className="text-right text-sm font-medium text-[#1A162F]">
                                                {formatCurrency(item)}
                                            </div>
                                            <div className="text-right text-sm font-normal text-[#666F8D]">
                                                {item.status || '--'}
                                            </div>
                                        </div>
                                    ))
                                ) : shouldShowBillingHistorySkeleton ? (
                                    <div className="p-8" aria-live="polite" aria-label={t('website.billing.billingHistoryLoading')}>
                                        <div className="space-y-3">
                                            <Skeleton className="h-[52px] w-full" />
                                            <Skeleton className="h-[52px] w-full" />
                                            <Skeleton className="h-[52px] w-full" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-16 text-center flex flex-col items-center justify-center">
                                        <p className="text-sm font-normal text-[#666F8D]">
                                            {t('website.billing.billingHistoryUnavailable')}
                                        </p>
                                    </div>
                                )}
                        
                            </div>
                            </div>
                            </div>
                            {totalBillingHistoryItems > 0 ? (
                                <div className="flex items-center justify-between border-t border-[#ECEFF5] bg-[#FCFCFC] px-6 py-4">
                                    <span className="text-sm font-normal text-[#666F8D]">
                                        {t('website.billing.showingHistoryRange', {
                                            start: formatIntegerForDisplay(
                                                (billingHistoryPage - 1) * BILLING_HISTORY_ITEMS_PER_PAGE + 1,
                                                locale,
                                            ),
                                            end: formatIntegerForDisplay(
                                                Math.min(
                                                    billingHistoryPage * BILLING_HISTORY_ITEMS_PER_PAGE,
                                                    totalBillingHistoryItems,
                                                ),
                                                locale,
                                            ),
                                            total: formatIntegerForDisplay(totalBillingHistoryItems, locale),
                                        })}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setBillingHistoryPage((page) => Math.max(1, page - 1))}
                                            disabled={billingHistoryPage === 1 || isTransactionsLoading}
                                            className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CaretLeft size={16} weight="regular" />
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {visibleBillingHistoryPages.map((page) => (
                                                <button
                                                    key={page}
                                                    type="button"
                                                    onClick={() => setBillingHistoryPage(page)}
                                                    disabled={isTransactionsLoading}
                                                    className={clsx(
                                                        "h-8 w-8 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                                        billingHistoryPage === page
                                                            ? "bg-[#1A162F] text-white hover:bg-primary/90"
                                                            : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]",
                                                    )}
                                                >
                                                    {page}
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setBillingHistoryPage((page) =>
                                                    Math.min(totalBillingHistoryPages, page + 1),
                                                )
                                            }
                                            disabled={
                                                billingHistoryPage === totalBillingHistoryPages ||
                                                isTransactionsLoading
                                            }
                                            className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CaretRight size={16} weight="regular" />
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default BillingSettings;
